<template>
  <div class="app-container">
    <el-card shadow="never" class="query-card">
      <el-form :model="query" ref="queryForm" :inline="true" v-show="showSearch">
        <el-form-item label="关键字" prop="keyword">
          <el-input
            v-model="query.keyword"
            placeholder="请输入参数键名或备注"
            clearable
            size="small"
            style="width: 240px"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" size="mini" @click="handleQuery">搜索</el-button>
          <el-button icon="el-icon-refresh" size="mini" @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>

      <el-row :gutter="10" class="mb8">
        <el-col :span="1.5">
          <el-button
            type="primary"
            plain
            icon="el-icon-plus"
            size="mini"
            @click="handleAdd"
          >新增</el-button>
        </el-col>
        <el-col :span="1.5">
          <el-button
            type="success"
            plain
            icon="el-icon-edit"
            size="mini"
            :disabled="single"
            @click="handleUpdate"
          >修改</el-button>
        </el-col>
        <el-col :span="1.5">
          <el-button
            type="danger"
            plain
            icon="el-icon-delete"
            size="mini"
            :disabled="multiple"
            @click="handleDelete"
          >删除</el-button>
        </el-col>
        <el-col :span="1.5">
          <el-button
            type="warning"
            plain
            icon="el-icon-download"
            size="mini"
            @click="handleExport"
          >导出</el-button>
        </el-col>
        <div class="right-tool">
          <el-tooltip class="item" effect="dark" :content="showSearch ? '隐藏搜索' : '显示搜索'" placement="top">
            <el-button size="mini" circle icon="el-icon-search" @click="showSearch=!showSearch" />
          </el-tooltip>
          <el-tooltip class="item" effect="dark" content="刷新" placement="top">
            <el-button size="mini" circle icon="el-icon-refresh" @click="fetchList" />
          </el-tooltip>
        </div>
      </el-row>

      <el-table v-loading="loading" :data="configList" @selection-change="handleSelectionChange" border>
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column label="参数主键" prop="id" width="100" />
        <el-table-column label="参数键名" prop="configKey" :show-overflow-tooltip="true" />
        <el-table-column label="参数键值" prop="configValue" :show-overflow-tooltip="true" />
        <el-table-column label="状态" align="center" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="mini">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="备注" prop="remark" :show-overflow-tooltip="true" />
        <el-table-column label="创建时间" align="center" prop="createdAt" width="180">
          <template slot-scope="scope">
            <span>{{ parseTime(scope.row.createdAt) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" align="center" class-name="small-padding fixed-width" width="150">
          <template slot-scope="scope">
            <div class="operation-cell">
              <el-button
                size="mini"
                type="text"
                icon="el-icon-edit"
                @click="handleUpdate(scope.row)"
              >修改</el-button>
              <el-button
                size="mini"
                type="text"
                icon="el-icon-delete"
                class="danger-text"
                @click="handleDelete(scope.row)"
              >删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          :current-page="pageNo"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 添加或修改参数配置对话框 -->
    <el-dialog :title="title" :visible.sync="open" width="500px" append-to-body>
      <el-form ref="form" :model="form" :rules="rules" label-width="80px">
        <el-form-item label="参数键名" prop="configKey">
          <el-input v-model="form.configKey" placeholder="请输入参数键名" />
        </el-form-item>
        <el-form-item label="参数键值" prop="configValue">
          <el-input v-model="form.configValue" placeholder="请输入参数键值" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" placeholder="请输入内容" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="submitForm">确 定</el-button>
        <el-button @click="cancel">取 消</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listConfigs, createConfig, updateConfig, deleteConfig } from '@/api/system/config';

export default {
  name: "Config",
  data() {
    return {
      // 遮罩层
      loading: true,
      // 选中数组
      ids: [],
      // 非单个禁用
      single: true,
      // 非多个禁用
      multiple: true,
      // 显示搜索条件
      showSearch: true,
      // 总条数
      total: 0,
      // 参数表格数据
      configList: [],
      // 弹出层标题
      title: "",
      // 是否显示弹出层
      open: false,
      // 查询参数
      query: {
        keyword: undefined
      },
      pageNo: 1,
      pageSize: 10,
      // 表单参数
      form: {},
      // 表单校验
      rules: {
        configKey: [
          { required: true, message: "参数键名不能为空", trigger: "blur" }
        ],
        configValue: [
          { required: true, message: "参数键值不能为空", trigger: "blur" }
        ]
      }
    };
  },
  created() {
    this.fetchList();
  },
  methods: {
    /** 查询参数列表 */
    async fetchList() {
      this.loading = true;
      try {
        const params = {
          pageNo: this.pageNo,
          pageSize: this.pageSize,
          ...this.query
        };
        const response = await listConfigs(params);
        this.configList = response.data.records;
        this.total = response.data.total;
      } finally {
        this.loading = false;
      }
    },
    // 取消按钮
    cancel() {
      this.open = false;
      this.reset();
    },
    // 表单重置
    reset() {
      this.form = {
        id: undefined,
        configKey: undefined,
        configValue: undefined,
        remark: undefined,
        status: 1
      };
    },
    /** 搜索按钮操作 */
    handleQuery() {
      this.pageNo = 1;
      this.fetchList();
    },
    /** 重置按钮操作 */
    resetQuery() {
      this.query = {
        keyword: undefined
      };
      this.handleQuery();
    },
    // 多选框选中数据
    handleSelectionChange(selection) {
      this.ids = selection.map(item => item.id);
      this.single = selection.length != 1;
      this.multiple = !selection.length;
    },
    /** 新增按钮操作 */
    handleAdd() {
      this.reset();
      this.open = true;
      this.title = "添加参数";
    },
    /** 修改按钮操作 */
    handleUpdate(row) {
      this.reset();
      const configId = row.id || this.ids;
      const target = row.id ? row : this.configList.find(i => i.id === configId[0]);
      if (!target) return;
      this.form = {
        id: target.id,
        configKey: target.configKey,
        configValue: target.configValue,
        remark: target.remark,
        status: typeof target.status === 'number' ? target.status : 1
      };
      this.open = true;
      this.title = "修改参数";
    },
    /** 提交按钮 */
    submitForm() {
      this.$refs["form"].validate(async valid => {
        if (valid) {
          const payload = {
            configKey: this.form.configKey ? this.form.configKey.trim() : '',
            configValue: this.form.configValue === undefined || this.form.configValue === null
              ? ''
              : String(this.form.configValue).trim(),
            remark: this.form.remark === undefined || this.form.remark === null
              ? ''
              : String(this.form.remark).trim(),
            status: this.form.status === 0 ? 0 : 1
          };
          if (this.form.id != undefined) {
             await updateConfig(this.form.id, payload);
             this.$message.success("修改成功");
          } else {
             await createConfig(payload);
             this.$message.success("新增成功");
          }
          this.open = false;
          this.fetchList();
        }
      });
    },
    /** 删除按钮操作 */
    handleDelete(row) {
      const configIds = row.id || this.ids;
      this.$confirm('是否确认删除参数编号为"' + configIds + '"的数据项？', "警告", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(async () => {
        if (Array.isArray(configIds)) {
            for(const id of configIds) {
                await deleteConfig(id);
            }
        } else {
            await deleteConfig(configIds);
        }
        this.fetchList();
        this.$message.success("删除成功");
      }).catch(() => {});
    },
    /** 导出按钮操作 */
    handleExport() {
      this.$message.info("导出功能待实现");
    },
    handlePageChange(page) {
      this.pageNo = page;
      this.fetchList();
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.fetchList();
    },
    parseTime(time) {
        if (!time) return '';
        const normalized = typeof time === 'string' ? time.replace(' ', 'T') : time;
        const date = new Date(normalized);
        if (Number.isNaN(date.getTime())) return String(time);
        const y = date.getFullYear();
        const m = (date.getMonth() + 1).toString().padStart(2, '0');
        const d = date.getDate().toString().padStart(2, '0');
        const h = date.getHours().toString().padStart(2, '0');
        const min = date.getMinutes().toString().padStart(2, '0');
        const s = date.getSeconds().toString().padStart(2, '0');
        return `${y}-${m}-${d} ${h}:${min}:${s}`;
    }
  }
};
</script>

<style scoped>
.app-container {
  padding: 0;
  background-color: #f8f8f9;
  min-height: calc(100vh - 84px);
}

.query-card {
  border: none;
}

.mb8 {
  margin-bottom: 8px;
}

.right-tool {
  float: right;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.el-table th {
  background-color: #f8f8f9 !important;
  color: #515a6e;
}

.el-form-item__label {
  font-weight: 700;
}

.danger-text {
  color: #ff4d4f !important;
}

.danger-text:hover {
  color: #ff7875 !important;
}

.operation-cell {
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
}

.operation-cell .el-button {
  margin: 0 5px;
}
</style>
